import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { UnlockDateTimePicker } from "./UnlockDateTimePicker";

export type DeliveryMode = "once" | "keep" | "unlock_later";

// Shared by SendGiftScreen.tsx's Custom Gift tab and SendGiftDialog.tsx
// (Lightbox's contextual "Send as Gift") so both get the same delivery-
// mode UI instead of a second, drifting copy. Three equal, mutually
// exclusive cards - "Unlock later" is a first-class mode now, not a toggle
// nested inside "Keep" (the previous design). All state (deliveryMode/
// date/time) is owned by the caller, same lifted-state shape the old
// viewOnce/locked/unlockAt trio already used.
export function GiftRetentionSelector({
  deliveryMode,
  dateValue,
  timeValue,
  minDate,
  dateTimeError,
  sending,
  onSelectMode,
  onDateChange,
  onTimeChange,
}: {
  deliveryMode: DeliveryMode;
  dateValue: string;
  timeValue: string;
  minDate: string;
  dateTimeError: string | null;
  sending: boolean;
  onSelectMode: (mode: DeliveryMode) => void;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        How should it be kept?
      </Typography>
      <div className="send-gift-mode-cards send-gift-mode-cards-3">
        <button
          type="button"
          className={`send-gift-mode-card${deliveryMode === "once" ? " is-selected" : ""}`}
          aria-pressed={deliveryMode === "once"}
          onClick={() => onSelectMode("once")}
          disabled={sending}
        >
          <span className="send-gift-mode-card-icon">
            <i className="bi bi-eye-fill" aria-hidden="true" />
          </span>
          <span className="send-gift-mode-card-body">
            <span className="send-gift-mode-card-title">View once</span>
            <span className="send-gift-mode-card-subtitle">Disappears after viewing</span>
          </span>
          {deliveryMode === "once" && <i className="bi bi-check-circle-fill send-gift-mode-card-check" aria-hidden="true" />}
        </button>
        <button
          type="button"
          className={`send-gift-mode-card${deliveryMode === "keep" ? " is-selected" : ""}`}
          aria-pressed={deliveryMode === "keep"}
          onClick={() => onSelectMode("keep")}
          disabled={sending}
        >
          <span className="send-gift-mode-card-icon">
            <i className="bi bi-bookmark-fill" aria-hidden="true" />
          </span>
          <span className="send-gift-mode-card-body">
            <span className="send-gift-mode-card-title">Keep</span>
            <span className="send-gift-mode-card-subtitle">Stays available</span>
          </span>
          {deliveryMode === "keep" && <i className="bi bi-check-circle-fill send-gift-mode-card-check" aria-hidden="true" />}
        </button>
        <button
          type="button"
          className={`send-gift-mode-card${deliveryMode === "unlock_later" ? " is-selected" : ""}`}
          aria-pressed={deliveryMode === "unlock_later"}
          onClick={() => onSelectMode("unlock_later")}
          disabled={sending}
        >
          <span className="send-gift-mode-card-icon">
            <i className="bi bi-lock-fill" aria-hidden="true" />
          </span>
          <span className="send-gift-mode-card-body">
            <span className="send-gift-mode-card-title">Unlock later</span>
            <span className="send-gift-mode-card-subtitle">Stays locked until a chosen date and time</span>
          </span>
          {deliveryMode === "unlock_later" && <i className="bi bi-check-circle-fill send-gift-mode-card-check" aria-hidden="true" />}
        </button>
      </div>

      {deliveryMode === "unlock_later" && (
        <Box sx={{ mt: 1.5 }}>
          <UnlockDateTimePicker
            dateValue={dateValue}
            timeValue={timeValue}
            minDate={minDate}
            error={dateTimeError}
            onDateChange={onDateChange}
            onTimeChange={onTimeChange}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {deliveryMode === "unlock_later"
          ? "Stays sealed until that date, then opens like a normal gift."
          : deliveryMode === "once"
            ? "Disappears forever right after it's opened."
            : "Stays in their Gifts inbox to keep or save later."}
      </Typography>
    </Box>
  );
}
